import { isRouteErrorResponse, useRouteError } from "react-router";
import { lazy, useState } from "react";
import { Button } from "components/ui/Button";

// Local Imports
import { Loadable } from "components/shared/Loadable";

// ----------------------------------------------------------------------

const app = {
  401: lazy(() => import("./401")),
  404: lazy(() => import("./404")),
  429: lazy(() => import("./429")),
  500: lazy(() => import("./500")),
};

function RootErrorBoundary() {
  const error = useRouteError();
  const [showDetails, setShowDetails] = useState(false);

  // Log error to console for debugging
  console.error("Application error:", error);

  if (isRouteErrorResponse(error)) {
    const Component = Loadable(app[error.status] || app[500]);
    return <Component error={error} />;
  }

  // For non-route errors (JavaScript errors, etc.)
  const errorMessage = error?.message || "An unexpected error occurred";
  const errorStack = error?.stack || "";
  const errorName = error?.name || "Error";
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
      <div className="w-full max-w-3xl p-8 bg-white rounded-lg shadow-lg">
        <div className="flex items-center text-red-600 mb-4">
          <svg className="w-10 h-10 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h1 className="text-2xl font-bold">Application Error</h1>
        </div>

        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">{errorName}</h2>
          <p className="text-gray-700">{errorMessage}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Button onClick={() => window.location.reload()}>
            Reload Page
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.location.href = '/'}
          >
            Go to Home
          </Button>
          <Button 
            variant="secondary"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
        </div>

        {showDetails && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2">Error Details</h3>
            <div className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
              <p className="font-mono text-sm whitespace-pre-wrap">{errorStack}</p>
            </div>
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Component Tree:</h4>
              <div className="bg-gray-100 p-4 rounded overflow-auto max-h-60">
                <p className="font-mono text-sm">
                  {error?.componentStack || "Component stack not available"}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-sm text-gray-500">
          <p>If this problem persists, please contact your administrator or try again later.</p>
        </div>
      </div>
    </div>
  );
}

export default RootErrorBoundary;